import { Button } from '@/components/ui/button';
import {
  TableOptions,
  flexRender,
  SortingState,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getSortedRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Fragment, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  FilterIcon,
  Plus,
  Search,
  Settings2,
} from 'lucide-react';
import { UseQueryOptions } from '@tanstack/react-query';
import Loader from '@/components/Loader';
import { FormFieldSelect, Option } from '@/components/FormFieldSelect';
import { FormProvider, useForm } from 'react-hook-form';
import FormFieldComponent from './FormFieldComponent';
import FormFieldCalendar from './FormFieldCalendar';
import FormFieldMultiSelect from './FormFieldMultiSelect';
import { AdvanceFilter } from '@/types/types';
import { AdvanceFilterButton } from './AdvanceFilterComponents';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type Params = {
  pageNo: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: string;
  filters?: AdvanceFilter[];
  [key: string]: string | number | undefined | AdvanceFilter[] | string[] | Date;
};

interface MultiSelectOption {
  value: string;
  label1: string;
  label2?: string;
}

export type DataTableSearchConfig = {
  label: string;
  placeholder?: string;
  name: string;
  defaultValue?: string | null;
  fieldType?: 'INPUT' | 'SELECT' | 'MULTISELECT' | 'DATE';
  stickyItem?: Option | null;
  options?: Option[];
  disabledAt?: (date: Date) => boolean;
  multiSelectOptions?: MultiSelectOption[];
  labelToDisplay?: { dropdown: 'label1' | 'label2'; selectedOption: 'label1' | 'label2' };
  dependsOn?: {
    field: string;
    resetOnChange?: boolean;
    disableBefore?: boolean;
  };
};

export interface ToolbarFilterOption {
  label: string;
  value: string | null;
}

export interface ToolbarFilterGroup {
  name: string;
  label: string;
  icon?: React.ReactElement;
  options: ToolbarFilterOption[];
  defaultValue?: string | null;
}

export interface ToolbarFiltersConfig {
  buttonLabel?: string;
  icon?: React.ReactElement;
  groups: ToolbarFilterGroup[];
}

type ConditionalApiGet<TData, TSecondaryData> =
  | {
      rows?: (TData | TSecondaryData)[];
      apiGet?: never;
    }
  | {
      rows?: TData[];
      isFetching?: boolean;
      apiGet: (
        params?: unknown,
        options?: { query?: UseQueryOptions<unknown> }
      ) => { data: (TData | TSecondaryData)[]; totalRows: number; isFetching: boolean };
    };

type DataTableProps<TData, TSecondaryData = unknown> = Omit<TableOptions<TData>, 'getCoreRowModel' | 'data'> &
  ConditionalApiGet<TData, TSecondaryData> & {
    columns: ColumnDef<TData>[];
    showPagination?: boolean;
    clickHandler?: boolean;
    openSecondaryColumns?: { id: string; isExpanded: boolean }[];
    secondaryColumns?: (rows?: TSecondaryData[]) => ColumnDef<TSecondaryData>[];
    secondaryData?: { id: string; data: TSecondaryData[] }[];
    hideHeaderRow?: boolean;
    hideSecondaryHeaderRow?: boolean;
    onRowClick?: (row: TData, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
    onSecondaryRowClick?: (row: TSecondaryData, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
    refreshKey?: number;
    stickyHeader?: boolean;
    rows?: TData[] | TSecondaryData[];
    setRows?: (data: TData[] | TSecondaryData[] | unknown[], rest?: unknown) => void;
    toolbar?: {
      toolBarTitle?: string;
      addButton?: boolean;
      addButtonTitle?: string;
      addOnClick?: () => void;
      filters?: ToolbarFiltersConfig;
      serviceSwitcher?: React.ReactElement;
    };
    isSecondaryTable?: boolean;
    secondaryTableId?: string;
    search?: DataTableSearchConfig[];
    onSearch?: (search: Record<string, string | string[] | Date | undefined> | undefined) => void;
    className?: string;
    showNRecords?: boolean;
    customPageSize?: number;
    getSecondaryTableRowId?: (row: TSecondaryData) => string;
    advanceFilter?: {
      filters: AdvanceFilter[];
      setIsAdvanceFilterOpen: React.Dispatch<SetStateAction<boolean>>;
    };
    filters?: Record<string, string | number | boolean | string[] | undefined>;
    summarySection?: React.ReactNode;
  };

export const DataTable = <TData, TSecondaryData>({
  columns,
  apiGet,
  secondaryData,
  showPagination = true,
  openSecondaryColumns,
  hideHeaderRow = false,
  hideSecondaryHeaderRow = false,
  secondaryColumns,
  onRowClick,
  onSecondaryRowClick,
  refreshKey = 0,
  stickyHeader = false,
  toolbar,
  rows,
  isSecondaryTable = false,
  secondaryTableId,
  setRows,
  search,
  onSearch,
  className,
  showNRecords,
  customPageSize,
  getSecondaryTableRowId,
  advanceFilter,
  filters,
  summarySection,
  ...props
}: DataTableProps<TData, TSecondaryData>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [secondarySorting, setSecondarySorting] = useState<SortingState>([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(customPageSize ?? 10);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [toolbarFilterValues, setToolbarFilterValues] = useState<Record<string, string | null | undefined>>(() => {
    const initial: Record<string, string | null | undefined> = {};
    if (toolbar?.filters?.groups?.length) {
      toolbar.filters.groups.forEach((group) => {
        // Only auto-select first option for compact variant (single group without buttonLabel)
        const isCompactVariant = toolbar.filters.groups.length === 1 && !toolbar.filters.buttonLabel;
        initial[group.name] =
          group.defaultValue !== undefined
            ? group.defaultValue
            : isCompactVariant
              ? group.options?.[0]?.value
              : undefined;
      });
    }
    return initial;
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const searchDefaultValues = search
    ? search.reduce(
        (acc, s) => {
          if (s.defaultValue != null) acc[s.name] = s.defaultValue;
          return acc;
        },
        {} as Record<string, string>
      )
    : {};
  const form = useForm({ defaultValues: searchDefaultValues });
  const { reset, getValues, control, setValue, watch } = form;
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [searchField, setSearchField] = useState<Record<string, string | string[] | Date | undefined> | undefined>(
    undefined
  );
  const [filterRefreshKey, setFilterRefreshKey] = useState(0);

  const dependentFields = useMemo(() => {
    if (!search) return [];
    return search.filter((s) => s.dependsOn);
  }, [search]);

  const dependencySourceFields = useMemo(() => {
    return dependentFields.map((f) => f.dependsOn?.field).filter(Boolean) as string[];
  }, [dependentFields]);

  const watchedDependencyValues = watch(dependencySourceFields) as (string | Date | undefined)[];

  useEffect(() => {
    dependentFields.forEach((field, index) => {
      const sourceValue: string | Date | undefined = watchedDependencyValues[index];
      const currentValue = getValues(field.name) as string | Date | undefined;

      if (field.dependsOn?.resetOnChange && currentValue !== undefined) {
        if (
          !sourceValue ||
          (sourceValue instanceof Date &&
            typeof currentValue === 'object' &&
            currentValue instanceof Date &&
            sourceValue > currentValue)
        ) {
          setValue(field.name, undefined);
        }
      }
    });
  }, [watchedDependencyValues, dependentFields, setValue, getValues]);

  const handleSecondarySortToggle = (field: string) => {
    setSecondarySorting((prev) => {
      const currentSort = prev?.find((s) => s.id === field);
      if (!currentSort) return [{ id: field, desc: false }];
      if (!currentSort.desc) return [{ id: field, desc: true }];

      return [];
    });
  };

  const getNestedProperty = (obj: TData | TSecondaryData, path: string): unknown => {
    return path.split('.').reduce((acc: unknown, key: string) => {
      if (acc && typeof acc === 'object' && key in acc) {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  };

  const sortedSecondaryData = useMemo(() => {
    if (!secondarySorting) return secondaryData;
    if (!secondaryData || !secondarySorting.length) return secondaryData;

    const { id, desc } = secondarySorting[0];

    return secondaryData.map((dataGroup) => {
      if (dataGroup.id === secondaryTableId && getSecondaryTableRowId) {
        return {
          ...dataGroup,
          data: dataGroup.data.sort((a, b) => {
            const aValue = getNestedProperty(a, id) as string | number;
            const bValue = getNestedProperty(b, id) as string | number;
            if (aValue > bValue) return desc ? -1 : 1;
            if (aValue < bValue) return desc ? 1 : -1;
            return 0;
          }),
        };
      }
      return dataGroup;
    });
  }, [secondaryData, secondarySorting]);

  const query = useMemo(() => {
    const buildQuery = {
      pageNo,
      pageSize,
      sortField,
      sortDirection,
      filters: advanceFilter?.filters,
    } as Params;
    if (pageNo) {
      buildQuery.pageNo = pageNo;
    }
    if (pageSize) {
      buildQuery.pageSize = pageSize;
    }
    if (sortField) {
      buildQuery.sortField = sortField;
    }
    if (sortDirection) {
      buildQuery.sortDirection = sortDirection;
    }

    if (advanceFilter?.filters) buildQuery.filters = advanceFilter.filters;

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          buildQuery[key] = value as string | number | string[];
        }
      });
    }
    if (toolbarFilterValues) {
      Object.entries(toolbarFilterValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          buildQuery[key] = value;
        }
      });
    }
    if (searchField) {
      const filterFields = new Set(advanceFilter?.filters?.map((f) => f.field) ?? []);
      Object.entries(searchField).forEach(([key, value]) => {
        if (filterFields.has(key)) return;
        if (value instanceof Date) {
          buildQuery[key] = value;
        } else if (Array.isArray(value) && value.length > 0) {
          buildQuery[key] = value;
        } else if (typeof value === 'string' && value.length > 0) {
          buildQuery[key] = value.trim();
        }
      });
    }

    return buildQuery;
  }, [pageNo, pageSize, sortField, sortDirection, toolbarFilterValues, searchField, advanceFilter?.filters, filters]);

  const apiResult = apiGet
    ? apiGet(query, {
        query: {
          queryKey: [
            pageSize,
            pageNo,
            sortField,
            sortDirection,
            refreshKey,
            searchField,
            advanceFilter?.filters,
            filters,
            toolbarFilterValues,
          ],
          refetchOnWindowFocus: false,
        },
      })
    : { data: (rows ?? []) as TData[] | TSecondaryData[], totalRows: 0, isFetching: false };

  const { data, totalRows, isFetching, ...rest } = apiResult;

  useEffect(() => {
    if (!isFetching) {
      setTotalRowsCount(totalRows);
    }
  }, [totalRows, isFetching]);

  useEffect(() => {
    if (data) {
      setRows && setRows(data as TData[], rest);
    }
  }, [data]);

  useEffect(() => {
    setPageNo(1);
  }, [filters]);

  useEffect(() => {
    if (!search?.length || !advanceFilter?.filters) {
      return;
    }

    search.forEach((searchField) => {
      const filterValue = advanceFilter.filters.find((filter) => filter.field === searchField.name)?.value;
      setValue(searchField.name, filterValue ?? '');
    });
  }, [advanceFilter?.filters, search, setValue]);

  const table = useReactTable<TData | TSecondaryData>({
    ...(props as unknown as TableOptions<TData | TSecondaryData>),
    data: data || [],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: columns as ColumnDef<TData | TSecondaryData, any>[],
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    state: {
      sorting: isSecondaryTable ? secondarySorting : sorting,
    },
    onSortingChange: isSecondaryTable ? setSecondarySorting : setSorting,
  });

  const totalPages = Math.ceil(totalRowsCount / pageSize);
  const handleSortToggle = (field: string) => {
    setSortField(field);
    setSortDirection((prevDirection) => {
      if (prevDirection === null) return 'asc';
      if (prevDirection === 'asc') return 'desc';
      if (prevDirection === 'desc') {
        setSortField(undefined);
        return null;
      }
      return null;
    });
  };
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNo(1);
  };
  return (
    <div className="w-[100%] h-fit flex flex-col gap-6">
      <FormProvider {...form}>
        {toolbar && (
          <div className="flex flex-row justify-between w-full pb-6 gap-6 items-center" id="toolbar">
            <h1 className="text-2xl font-semibold  xs:text-2xl">{toolbar?.toolBarTitle}</h1>
            <div className="flex flex-row gap-4">
              {toolbar?.serviceSwitcher && toolbar.serviceSwitcher}
              {toolbar?.filters && (
                <>
                  {toolbar.filters.groups.length === 1 && !toolbar.filters.buttonLabel ? (
                    <>
                      {toolbar.filters.groups.map((group) => (
                        <FormFieldSelect
                          key={group.name}
                          icon={group.icon ?? <FilterIcon className="h-4 w-4 text-[#969696]" />}
                          name={group.name}
                          data-testid="filter-select"
                          className="flex flex-row w-fit h-[36px] p-2 gap-6"
                          defaultValue={
                            toolbarFilterValues[group.name] !== undefined
                              ? toolbarFilterValues[group.name]
                              : group.defaultValue !== undefined
                                ? group.defaultValue
                                : group.options[0]?.value
                          }
                          hasControl={false}
                          options={group.options}
                          onChange={(value) =>
                            setToolbarFilterValues((prev) => ({
                              ...prev,
                              [group.name]: value,
                            }))
                          }
                        />
                      ))}
                    </>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        asChild
                        className="data-[state=open]:bg-[#EDF5FF] bg-white !border-[0px] min-w-[87px] w-fit h-[36px] !text-primary font-[500] text-sm"
                      >
                        <Button
                          className="flex flex-row gap-2 !focus:bg-[#EDF5FF] align-middle border-1"
                          variant="outline"
                        >
                          <span>{toolbar.filters.buttonLabel ?? 'Filters'}</span>
                          {toolbar?.filters?.icon ?? (
                            <Settings2 className="w-4 h-4" color="#0A2240" strokeWidth="1px" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent className="w-56 mr-8">
                        {toolbar.filters.groups.map((group, index) => (
                          <Fragment key={group.name}>
                            {index > 0 && <DropdownMenuSeparator />}

                            <DropdownMenuLabel className="pl-8 text-sm font-[500]">{group.label}</DropdownMenuLabel>

                            {group.options.map((option) => (
                              <DropdownMenuCheckboxItem
                                key={option.value ?? 'null'}
                                className={`text-placeholder ${
                                  toolbarFilterValues[group.name] === option.value &&
                                  'text-primary bg-[#EDF5FF] font-[500]'
                                }`}
                                checked={toolbarFilterValues[group.name] === option.value}
                                onCheckedChange={() =>
                                  setToolbarFilterValues((prev) => ({
                                    ...prev,
                                    [group.name]: option.value,
                                  }))
                                }
                              >
                                {option.label}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </Fragment>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}

              {toolbar?.addButton && (
                <Button
                  onClick={() => {
                    if (toolbar.addOnClick) {
                      toolbar.addOnClick();
                    }
                  }}
                  className="p-3 hover:bg-[#12375C] w-fit h-[36px]"
                  data-testid="add-button"
                >
                  <div className="flex flex-row items-center">
                    <Plus className="h-4" />
                    <span className="font-medium hidden sm:block">{toolbar?.addButtonTitle}</span>
                  </div>
                </Button>
              )}
            </div>
          </div>
        )}
        {search && (
          <div className="flex flex-col gap-6">
            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6" key={filterRefreshKey}>
              {search?.map((searchField) => {
                switch (searchField.fieldType) {
                  case 'SELECT':
                    return (
                      <FormFieldSelect
                        name={searchField.name}
                        label={searchField.label}
                        options={searchField.options ?? []}
                        placeholder={searchField.placeholder}
                        clearable
                        stickyItem={searchField.stickyItem}
                        icon={<Search className="h-5 w-5 text-[#969696] mt-[-3px]" />}
                        className="flex flex-row  p-2 gap-6"
                        key={searchField.name}
                        onChange={() => setFilterRefreshKey((prev) => prev + 1)}
                      />
                    );
                  case 'MULTISELECT':
                    return (
                      <FormFieldMultiSelect
                        key={searchField.name}
                        name={searchField.name}
                        label={searchField.label ?? 'Search'}
                        placeholder={searchField.placeholder}
                        options={searchField.multiSelectOptions ?? []}
                        labelToDisplay={searchField.labelToDisplay ?? { dropdown: 'label1', selectedOption: 'label2' }}
                      />
                    );
                  case 'DATE': {
                    const dependsOnField = searchField.dependsOn?.field;
                    const dependsOnValue = dependsOnField
                      ? (watch(dependsOnField) as string | Date | undefined)
                      : undefined;
                    const combinedDisabledAt = (date: Date) => {
                      if (searchField.disabledAt?.(date)) return true;
                      if (searchField.dependsOn?.disableBefore && dependsOnValue instanceof Date) {
                        const dependsOnDate = new Date(dependsOnValue);
                        dependsOnDate.setHours(0, 0, 0, 0);
                        const compareDate = new Date(date);
                        compareDate.setHours(0, 0, 0, 0);
                        return compareDate < dependsOnDate;
                      }
                      return false;
                    };
                    return (
                      <FormFieldCalendar
                        key={searchField.name}
                        name={searchField.name}
                        label={searchField.label ?? 'Select Date'}
                        placeholder={searchField.placeholder}
                        control={control}
                        clearable
                        disabledAt={combinedDisabledAt}
                      />
                    );
                  }
                  default:
                    return (
                      <FormFieldComponent
                        control={control}
                        key={searchField.name}
                        data-testid={`search-${searchField.name}`}
                        name={searchField.name}
                        label={searchField.label}
                        placeholder={searchField.placeholder}
                        props={{
                          Icon: Search,
                          iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
                        }}
                      />
                    );
                }
              })}
            </div>
            <div className="flex sm:flex-row flex-col justify-start gap-6">
              <Button
                type="button"
                variant="outline"
                className="h-[36px] py-2 px-4"
                onClick={() => {
                  const resetValues: Record<string, string | string[] | null | undefined> = {};
                  search.forEach((s) => {
                    if (s.fieldType === 'MULTISELECT') {
                      resetValues[s.name] = [];
                    } else if (s.fieldType === 'DATE') {
                      resetValues[s.name] = undefined;
                    } else {
                      resetValues[s.name] = '';
                    }
                  });
                  if (toolbar?.filters?.groups) {
                    const newToolbarFilterValues: Record<string, string | undefined> = {};
                    if (toolbar.filters.groups.length === 1) {
                      toolbar.filters.groups.forEach((group) => {
                        newToolbarFilterValues[group.name] = group.defaultValue ?? group.options[0]?.value ?? undefined;
                      });
                    } else {
                      toolbar.filters.groups.forEach((group) => {
                        newToolbarFilterValues[group.name] = undefined;
                      });
                    }
                    setToolbarFilterValues(newToolbarFilterValues);
                  }

                  reset({
                    ...getValues(),
                    ...resetValues,
                  });
                  setSearchField(undefined);
                  setPageNo(1);
                  onSearch?.(undefined);
                  setFilterRefreshKey((prev) => prev + 1);
                }}
              >
                Reset
              </Button>
              {!!advanceFilter && (
                <AdvanceFilterButton
                  onClick={() => advanceFilter.setIsAdvanceFilterOpen((prev) => !prev)}
                  buttonLabel="Advanced Search"
                  filters={
                    advanceFilter.filters.filter((filter) => !search.some((field) => field.name === filter.field)) ?? []
                  }
                />
              )}
              <Button
                className="text-[#fff] h-[36px] py-2 px-4"
                onClick={() => {
                  setPageNo(1);
                  const newSearchField: Record<string, string | string[] | Date | undefined> = {};
                  search.forEach((s) => {
                    const value = getValues(s.name) as unknown;
                    if (s.fieldType === 'DATE' && value instanceof Date) {
                      newSearchField[s.name] = value;
                    } else if (s.fieldType === 'MULTISELECT' && Array.isArray(value)) {
                      newSearchField[s.name] = value as string[];
                    } else if (typeof value === 'string' || typeof value === 'undefined') {
                      newSearchField[s.name] = value;
                    } else {
                      newSearchField[s.name] = undefined;
                    }
                  });
                  onSearch?.(newSearchField);
                  setSearchField(newSearchField);
                }}
                disabled={isFetching}
              >
                Search
              </Button>
            </div>
          </div>
        )}
        {summarySection && <div className="w-full">{summarySection}</div>}
      </FormProvider>
      <Table
        className={`scrollbar-hide ${isSecondaryTable || hideHeaderRow ? 'border-none' : 'border-[1px] border-[#C6C6C6'} ${className ? className : ''} ${!isSecondaryTable ? 'rounded-md' : 'rounded-none'}`}
      >
        {!hideHeaderRow && (
          <TableHeader className="text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className={`${stickyHeader && 'sticky top-0 z-10'}`}>
                {headerGroup.headers.map((header, index) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={`flex-col bg-[#F5F5F5] justify-start  ${index == 0 ? 'px-4' : 'px-0'} ${isSecondaryTable ? 'py-[0px] h-5' : ''} `}
                    >
                      <div className="flex flex-row align-middle gap-2 min-h-[25px]">
                        <div
                          onClick={() => {
                            if (header.column.columnDef.enableSorting) {
                              if (!isSecondaryTable) handleSortToggle(header.column.id);
                              if (isSecondaryTable) handleSecondarySortToggle(header.column.id);
                            }
                          }}
                          className={`flex w-full align-middle items-center ${header.column.columnDef.enableSorting ? 'cursor-pointer' : 'cursor-default'} ${header.column.columnDef.enableSorting && ((header.column.id === sortField && (sortDirection === 'asc' || sortDirection === 'desc')) || secondarySorting?.[0]?.id === header.column.id) && 'text-[#3B82F6]'}`}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.columnDef.enableSorting && (
                            <div className="flex flex-col p-1 cursor-pointer">
                              <ChevronsUpDown
                                className={`(h-4 w-4 ${((header.column.id === sortField && (sortDirection === 'asc' || sortDirection === 'desc')) || secondarySorting?.[0]?.id === header.column.id) && 'text-[#3B82F6]'}`}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody className="!border-[0px]">
          {table.getRowModel().rows?.length && !isFetching ? (
            table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <TableRow
                  className={`h-10 cursor-pointer ${isSecondaryTable ? 'border-none' : ''} ${
                    getSecondaryTableRowId &&
                    openSecondaryColumns?.find(
                      (ow) => ow.id === getSecondaryTableRowId(row.original as TSecondaryData)
                    ) &&
                    sortedSecondaryData?.find((sd) => sd.id === getSecondaryTableRowId(row.original as TSecondaryData))
                      ?.data &&
                    secondaryColumns &&
                    'border-b-[0px]'
                  }`}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={(e) =>
                    !isSecondaryTable
                      ? onRowClick && onRowClick(row.original as TData, e)
                      : onSecondaryRowClick && onSecondaryRowClick(row.original as TSecondaryData, e)
                  }
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      data-testid={`data-table-row-${cell.id}`}
                      className={`${index === 0 ? 'px-4 pb-3 ' : 'px-0 '} break-words break-all text-ellipsis overflow-hidden ${onRowClick && 'cursor-pointer '}`}
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {getSecondaryTableRowId &&
                  openSecondaryColumns?.find(
                    (ow) => ow.id === getSecondaryTableRowId(row.original as TSecondaryData)
                  ) &&
                  sortedSecondaryData?.find((sd) => sd.id === getSecondaryTableRowId(row.original as TSecondaryData))
                    ?.data &&
                  secondaryColumns && (
                    <tr className="p-0 border-b-[1px]">
                      <td colSpan={row.getVisibleCells().length}>
                        <DataTable
                          columns={secondaryColumns(
                            sortedSecondaryData?.find(
                              (sd) => sd.id === getSecondaryTableRowId(row.original as TSecondaryData)
                            )?.data
                          )}
                          rows={
                            sortedSecondaryData?.find(
                              (sd) => sd.id === getSecondaryTableRowId(row.original as TSecondaryData)
                            )?.data
                          }
                          showPagination={false}
                          hideHeaderRow={hideSecondaryHeaderRow}
                          toolbar={undefined}
                          isSecondaryTable
                          secondaryData={secondaryData}
                          onSecondaryRowClick={onSecondaryRowClick}
                          secondaryTableId={getSecondaryTableRowId(row.original as TSecondaryData)}
                          getSecondaryTableRowId={getSecondaryTableRowId}
                        />
                      </td>
                    </tr>
                  )}
              </Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                <Loader isLoading={isFetching}>{'No Results'}</Loader>
              </TableCell>
            </TableRow>
          )}
          {pageSize < totalRowsCount && !isFetching && showNRecords && (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-10 text-center font-semibold ">
                + {totalRowsCount - pageSize} Records
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <div className="flex flex-col items-start justify-between py-4 gap-2 sm:flex-row sm:items-center">
          <div className="flex gap-2.5 items-center">
            <span className="font-medium text-xs xs:text-base pr-1">Rows per page</span>
            <Select value={pageSize.toString()} onValueChange={(value) => handlePageSizeChange(Number(value))}>
              <SelectTrigger
                className="w-[70px] h-[36px]"
                customArrow={<ChevronDown className="text-[#757575] h-4 w-4" />}
              >
                <SelectValue defaultValue="10" className="text-black" />
              </SelectTrigger>
              <SelectContent className="text-black">
                <SelectGroup>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem disabled={totalRowsCount <= 10} value="20">
                    20
                  </SelectItem>
                  <SelectItem disabled={totalRowsCount <= 20} value="50">
                    50
                  </SelectItem>
                  <SelectItem disabled={totalRowsCount <= 100} value="100">
                    100
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-row gap-2 items-center mt-2 sm:mt-0">
            <span className="flex font-medium text-xs xs:text-base pr-3">
              {!data.length ? 0 : (pageNo - 1) * pageSize + 1} -{' '}
              {pageSize * pageNo > totalRowsCount ? totalRowsCount : pageSize * pageNo} of {totalRowsCount}
            </span>

            <Button
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(1)}
              disabled={pageNo == 1}
            >
              <ChevronsLeft className="h-4 w-4" color="#0A0A0A" />
            </Button>
            <Button
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(pageNo - 1)}
              disabled={pageNo == 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(pageNo + 1)}
              disabled={pageNo === totalPages || data.length === 0 || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(totalPages)}
              disabled={pageNo === totalPages || data.length === 0 || isFetching}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
